import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, Alert } from 'antd';

import Text from '../../UI/formFieldsTypes/Text';
import Instruktor from '../../UI/formFieldsTypes/Instruktor';
import FormInput from '../../UI/formFieldsTypes/FormInput';
import FormTextArea from '../../UI/formFieldsTypes/FormTextArea';
import InlineTable from '../../UI/formFieldsTypes/InlineTable';
import HoursBilanceTable from '../../UI/formFieldsTypes/HoursBilanceTable';
import FormyZajecTable from '../../UI/formFieldsTypes/FormyZajecTable';
import RealizatorzyTable from '../../UI/formFieldsTypes/RealizatorzyTable';
import Hour from '../../UI/formFieldsTypes/Hour';
import { FormsContainer, FormHeader, JustifiedBox } from './FormContentRenderer.style';
import { UpdateFormDataPayload } from '../../../redux/dynamicTabs/kartaPrzedmiotu/interfaces';

interface ComponentMapper {
  [key: string]: any;
}
const componentMapper: ComponentMapper = {
  /** wartość tekstowa tylko do odczytu, nieedytowalna */
  text: Text,
  /** Kierownicy przedmiotu */
  instruktor: Instruktor,
  /** wartość czasowa */
  hour: Hour,
  /** wartość tekstowa, edytowalna */
  input: FormInput,
  /** długa wartość tekstowa */
  textArea: FormTextArea,
  /** tabela, w której użytkownik samodzielnie wpisuje wartości */
  inlineTable: InlineTable,
  /** tabela inline z dodatkowymi wartościami predefiniowanymi, których użytkownik nie może wpisać samodzielnie */
  predefinedTable: HoursBilanceTable,
  /** tabela form zajęć, w której użytkownik może edytować dane, występują różne typy inputów */
  customTable: FormyZajecTable,
  /** tabela pozwalająca określić realizatorów przedmiotu */
  listaRealizatorow: RealizatorzyTable,
};

interface Props {
  /** akcja aktualizująca dane formularza w stanie apikacji */
  // updateFormData: (payload: UpdateFormData['payload']) => void;
  updateFormData: (payload: UpdateFormDataPayload) => void;
  /** stan formularza w store */
  forms: FormDataSet;
  /** informacja czy komponent do drukowania */
  toPrint?: boolean;
}

/**
 * Komponent mapująca elementy formualarza karty przedmiotu w zależności od typu informacji
 *  każdy typ <Component /> otrzymuje te same właściwości
 *  - updateFormData - akcja aktualizująca dane formularza w stanie apikacji
 *  - numer indeksu
 *  - readOnly - informacja czy formularz znajduje się w trybe do odczytu
 *  - template - informacje o typie, dodatkowe właśności (np. lista kolumn)
 *  - value - wartość lub wartości danych
 *  - defaultValue - wartość lub wartości początkowe (takie same jak value) - służą do przechowywania danych do odczytu w trybie edycji
 *
 */
export default React.memo(function FormContentRenderer(props: Props): React.ReactElement {
  const { t } = useTranslation();
  const updateFormData = useCallback(props.updateFormData, [props]);
  const loading = props.forms.loadingTemplate || props.forms.loadingValues;

  if (props.forms.error) return <Alert message={t('UNKNOWED_ERROR_TITLE')} type='error' />;
  return loading ? (
    <Skeleton active={true} />
  ) : (
    <FormsContainer data-cy={props.toPrint ? undefined : 'subjectCardContainer'}>
      {props.forms.formTemplates.map((component, index): JSX.Element => {
        const Component = componentMapper[component.type];
        return Component && props.forms.originFormValues ? (
          <JustifiedBox
            key={index}
            style={props.toPrint ? { pageBreakInside: 'avoid' } : { pageBreakInside: 'auto' }}
            data-cy={component.title}
          >
            <FormHeader>{t(component.title)}</FormHeader>
            <Component
              updateFormData={updateFormData}
              index={index}
              readOnly={props.forms.readOnly}
              template={component}
              value={props.forms.formValues.find(valueObject => component.dataIndex === valueObject.dataIndex)}
              defaultValue={props.forms.originFormValues.find(valueObject => component.dataIndex === valueObject.dataIndex)}
            />
          </JustifiedBox>
        ) : (
          <div>
            <FormHeader>{t(component.title)}</FormHeader>
            <p>
              <Alert message={t('UNSUPPORTED_DATA_FORMAT')} type='error' />
            </p>
          </div>
        );
      })}
    </FormsContainer>
  );
});
